// src/pages/administrativo/ConfigDFeCad.js
import React, { useState, useEffect } from 'react';

import Input from '../../components/input/Input.js';
import { useConfigDFe } from '../../contexts/administrativo/ConfigDFeContext.js';
import ConfigDFeSearch from './ConfigDFeSearch';
import { ConfigDFe } from '../../models/administrativo/ConfigDFe.ts';
import { useMessage } from '../../contexts/geral/MessageContext.js';
import { useYesNo } from '../../contexts/geral/YesNoContext.js';
import Select from '../../components/input/Select.js';
import ModalContainer from '../../components/modalForm/ModalContainer.js';
import ModalHeader from '../../components/modalForm/ModalHeader.js';
import ModalBody from '../../components/modalForm/ModalBody.js';
import ModalFooter from '../../components/modalForm/ModalFooter.js';
import ConfigDFeCertificadoModal from './ConfigDFeCertificadoModal.js';
import ModalTabs from '../../components/modalForm/ModalTabs';
import ModalActions, { ModalActionButton } from '../../components/modalForm/ModalActions';

const ConfigDFeCad = ({ show, onHide }) => {
    const { configDFeList, addConfigDFe, updateConfigDFe, deleteConfigDFe } = useConfigDFe();
    const [configDFe, setConfigDFe] = useState(new ConfigDFe());
    const [showSearchModal, setShowSearchModal] = useState(false);
    const [showCertificadoModal, setShowCertificadoModal] = useState(false);
    const [abaAtiva, setAbaAtiva] = useState('geral');
    const [novoReg, setNovoReg] = useState(false);
    const { showMessage } = useMessage();
    const { showYesNo } = useYesNo();

    useEffect(() => {
        setConfigDFe(new ConfigDFe());
    }, []);

    const handleNovo = () => {
        setConfigDFe(new ConfigDFe());
        setNovoReg(true);
        setAbaAtiva('geral');
    };

    const handleEditar = (id) => {
        if (id === 0) {
            showMessage("Selecione uma configuração de DFe para editar!");
            return;
        }
        setNovoReg(false);
    };

    const handleDelete = async () => {
        if (!configDFe?.id) {
            showMessage("Selecione uma configuração de DFe para excluir!");
        } else {
            const resposta = await showYesNo("Deseja excluir a configuração de DFe selecionada?");
            if (resposta) {
                await deleteConfigDFe(configDFe.id);
                handleNovo();
            }
        }
    };

    const handleSave = async () => {
        try {
            const somenteNumeros = (valor) => String(valor ?? '').replace(/\D/g, '');
            if (configDFe.responsavelTecnicoCNPJ && somenteNumeros(configDFe.responsavelTecnicoCNPJ).length !== 14) {
                showMessage('Informe um CNPJ válido para o responsável técnico!', 'warning');
                return;
            }
            if (configDFe.cnpjAutorizadoXML && somenteNumeros(configDFe.cnpjAutorizadoXML).length !== 14) {
                showMessage('Informe um CNPJ autorizado válido!', 'warning');
                return;
            }
            if (configDFe.responsavelTecnicoEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(configDFe.responsavelTecnicoEmail)) {
                showMessage('Informe um e-mail válido para o responsável técnico!', 'warning');
                return;
            }
            const telefone = somenteNumeros(configDFe.responsavelTecnicoTelefone);
            if (telefone && ![10, 11].includes(telefone.length)) {
                showMessage('Informe um telefone válido para o responsável técnico!', 'warning');
                return;
            }
            const payload = {
                ...configDFe,
                responsavelTecnicoCNPJ: somenteNumeros(configDFe.responsavelTecnicoCNPJ),
                responsavelTecnicoTelefone: telefone,
                cnpjAutorizadoXML: somenteNumeros(configDFe.cnpjAutorizadoXML),
            };

            if (!configDFe.id) {
                await addConfigDFe(payload);
            } else {
                await updateConfigDFe(payload);
            }
            handleNovo();
        } catch (error) {
            console.error("Erro ao gravar dados: ", error);
        }
    };

    const handleExit = () => {
        const configSelecionada = configDFeList.find(c => c.id.toString() === configDFe?.id?.toString());

        if (configSelecionada) {
            setConfigDFe(configSelecionada);
            handleEditar(configDFe?.id);
        } else {
            setConfigDFe(new ConfigDFe());
        }
    };

    const getDiasValidadeCertificado = () => {
        if (!configDFe?.certificadoValidade) return null;

        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);

        const validade = new Date(configDFe.certificadoValidade);
        validade.setHours(0, 0, 0, 0);

        return Math.ceil((validade.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));
    };

    const formatarValidadeCertificado = () => {
        if (!configDFe?.certificadoValidade) return 'Não informado';

        return new Date(configDFe.certificadoValidade).toLocaleDateString('pt-BR');
    };

    if (!show) return null;

    return (
        <ModalContainer show={show} largura="850px" variant="cadastro" onHide={onHide}>
            <ModalHeader title={novoReg ? 'Nova Configuração de DFe' : 'Alterar Configuração de DFe'} onHide={onHide} />
            <ModalBody>
                <ModalTabs >
                    <li className="nav-item">
                        <button type="button" className={`nav-link ${abaAtiva === 'geral' ? 'active' : ''}`} onClick={() => setAbaAtiva('geral')}>
                            Geral
                        </button>
                    </li>
                    <li className="nav-item">
                        <button type="button" className={`nav-link ${abaAtiva === 'certificado' ? 'active' : ''}`} onClick={() => setAbaAtiva('certificado')}>
                            Certificado
                        </button>
                    </li>
                    <li className="nav-item">
                        <button type="button" className={`nav-link ${abaAtiva === 'mdfe' ? 'active' : ''}`} onClick={() => setAbaAtiva('mdfe')}>
                            MDF-e
                        </button>
                    </li>
                    <li className="nav-item">
                        <button type="button" className={`nav-link ${abaAtiva === 'nfse' ? 'active' : ''}`} onClick={() => setAbaAtiva('nfse')}>
                            NFS-e
                        </button>
                    </li>
                </ModalTabs>

                <form>
                    {abaAtiva === 'geral' && (
                        <>
                            <div className="input-group mb-3">
                                <Input
                                    label="Código"
                                    value={configDFe.id ?? ''}
                                    onChange={(e) => setConfigDFe({ ...configDFe, id: e.target.value })}
                                    onBlur={handleExit}
                                    disabled={!novoReg}
                                    btnBuscar={() => setShowSearchModal(true)}
                                />
                                <Input
                                    label="Número do Certificado"
                                    value={configDFe.numeroCertificado ?? ''}
                                    onChange={(e) => setConfigDFe({ ...configDFe, numeroCertificado: e.target.value })}
                                    size={300}
                                    autoFocus={true}
                                />
                            </div>
                            <div className="input-group">
                                <Input
                                    label="Id Token"
                                    value={configDFe.idToken ?? ''}
                                    onChange={(e) => setConfigDFe({ ...configDFe, idToken: e.target.value })}
                                    size={100}
                                />
                                <Input
                                    label="Código de Segurança do Certificado"
                                    value={configDFe.cscCertificado ?? ''}
                                    onChange={(e) => setConfigDFe({ ...configDFe, cscCertificado: e.target.value })}
                                    size={350}
                                />
                            </div>
                            <div className="input-group">
                                <Input
                                    label="Id Token Homolog"
                                    value={configDFe.idTokenHml ?? ''}
                                    onChange={(e) => setConfigDFe({ ...configDFe, idTokenHml: e.target.value })}
                                    size={100}
                                />
                                <Input
                                    label="Código de Segurança do Certificado Homologação"
                                    value={configDFe.cscCertificadoHml ?? ''}
                                    onChange={(e) => setConfigDFe({ ...configDFe, cscCertificadoHml: e.target.value })}
                                    size={350}
                                />
                            </div>
                            <div className="input-group">
                                <Input
                                    label="Filial"
                                    value={configDFe.filialId ?? ''}
                                    onChange={(e) => setConfigDFe({ ...configDFe, filialId: e.target.value })}
                                    size={100}
                                />
                                <Select
                                    label="Ambiente"
                                    value={configDFe.tpAmb}
                                    size={170}
                                    onChange={(e) => setConfigDFe({ ...configDFe, tpAmb: Number(e.target.value) })}
                                    options={[{ value: 1, label: 'Produção' }, { value: 2, label: 'Homologação' }]}
                                    style={{ marginRight: '20px' }}
                                />
                            </div>
                            <div className="input-group">
                                <Input
                                    label="Série da NFC-e"
                                    value={configDFe.serieNFCe ?? ''}
                                    onChange={(e) => setConfigDFe({ ...configDFe, serieNFCe: e.target.value })}
                                    size={100}
                                />
                                <Input
                                    label="Núm. NFC-e"
                                    value={configDFe.numDocNFCe ?? ''}
                                    onChange={(e) => setConfigDFe({ ...configDFe, numDocNFCe: e.target.value })}
                                    size={100}
                                />
                            </div>
                            <div className="input-group">
                                <Input
                                    label="Série da NF-e"
                                    value={configDFe.serieNFe ?? ''}
                                    onChange={(e) => setConfigDFe({ ...configDFe, serieNFe: e.target.value })}
                                    size={100}
                                />
                                <Input
                                    label="Núm. NF-e"
                                    value={configDFe.numDocNFe ?? ''}
                                    onChange={(e) => setConfigDFe({ ...configDFe, numDocNFe: e.target.value })}
                                    size={100}
                                />
                            </div>
                        </>
                    )}

                    {abaAtiva === 'mdfe' && (
                        <>
                            <div className="input-group">
                                <Input label="Série MDF-e" type="number" value={configDFe.serieMDFe ?? ''} onChange={(e) => setConfigDFe({ ...configDFe, serieMDFe: e.target.value === '' ? null : Number(e.target.value) })} size={140} />
                                <Input label="Próximo MDF-e" type="number" value={configDFe.numDocMDFe ?? ''} onChange={(e) => setConfigDFe({ ...configDFe, numDocMDFe: e.target.value === '' ? null : Number(e.target.value) })} size={140} />
                            </div>
                            <div className="input-group">
                                <Input label="Série MDF-e Homologação" type="number" value={configDFe.serieMDFeHml ?? ''} onChange={(e) => setConfigDFe({ ...configDFe, serieMDFeHml: e.target.value === '' ? null : Number(e.target.value) })} size={190} />
                                <Input label="Próximo MDF-e Homologação" type="number" value={configDFe.numDocMDFeHml ?? ''} onChange={(e) => setConfigDFe({ ...configDFe, numDocMDFeHml: e.target.value === '' ? null : Number(e.target.value) })} size={210} />
                            </div>
                            <div className="input-group">
                                <Input label="RNTRC" value={configDFe.rntrc ?? ''} onChange={(e) => setConfigDFe({ ...configDFe, rntrc: e.target.value })} size={150} />
                                <Input label="CNPJ autorizado no XML" mask="99.999.999/9999-99" value={configDFe.cnpjAutorizadoXML ?? ''} onChange={(e) => setConfigDFe({ ...configDFe, cnpjAutorizadoXML: e.target.value })} size={200} />
                            </div>
                        </>
                    )}

                    {abaAtiva === 'nfse' && (
                        <>
                            <div className="input-group">
                                <Input label="URL da API NFS-e" value={configDFe.urlApiNFSe ?? ''} onChange={(e) => setConfigDFe({ ...configDFe, urlApiNFSe: e.target.value })} size={380} />
                                <Input label="Token NFS-e" value={configDFe.tokenNFSe ?? ''} onChange={(e) => setConfigDFe({ ...configDFe, tokenNFSe: e.target.value })} size={300} />
                            </div>
                            <div className="input-group">
                                <Input label="URL da API NFS-e Homologação" value={configDFe.urlApiNFSeHml ?? ''} onChange={(e) => setConfigDFe({ ...configDFe, urlApiNFSeHml: e.target.value })} size={380} />
                                <Input label="Token NFS-e Homologação" value={configDFe.tokenNFSeHml ?? ''} onChange={(e) => setConfigDFe({ ...configDFe, tokenNFSeHml: e.target.value })} size={300} />
                            </div>
                            <div className="input-group">
                                <Input label="Série NFS-e" type="number" value={configDFe.serieNFSe ?? ''} onChange={(e) => setConfigDFe({ ...configDFe, serieNFSe: e.target.value === '' ? null : Number(e.target.value) })} size={140} />
                                <Input label="Próxima DPS NFS-e" type="number" value={configDFe.numDpsNFSe ?? ''} onChange={(e) => setConfigDFe({ ...configDFe, numDpsNFSe: e.target.value === '' ? null : Number(e.target.value) })} size={160} />
                            </div>
                            <div className="input-group">
                                <Input label="Série NFS-e Homologação" type="number" value={configDFe.serieNFSeHml ?? ''} onChange={(e) => setConfigDFe({ ...configDFe, serieNFSeHml: e.target.value === '' ? null : Number(e.target.value) })} size={190} />
                                <Input label="Próxima DPS NFS-e Homologação" type="number" value={configDFe.numDpsNFSeHml ?? ''} onChange={(e) => setConfigDFe({ ...configDFe, numDpsNFSeHml: e.target.value === '' ? null : Number(e.target.value) })} size={220} />
                            </div>
                            <div className="input-group">
                                <Input label="Opção pelo Simples Nacional" type="number" value={configDFe.opcaoSimplesNacionalNFSe ?? ''} onChange={(e) => setConfigDFe({ ...configDFe, opcaoSimplesNacionalNFSe: e.target.value === '' ? null : Number(e.target.value) })} size={200} />
                                <Input label="Regime Especial de Tributação" type="number" value={configDFe.regimeEspecialTributacaoNFSe ?? ''} onChange={(e) => setConfigDFe({ ...configDFe, regimeEspecialTributacaoNFSe: e.target.value === '' ? null : Number(e.target.value) })} size={220} />
                                <Input label="Regime de Apuração do Simples" type="number" value={configDFe.regimeApuracaoSimplesNFSe ?? ''} onChange={(e) => setConfigDFe({ ...configDFe, regimeApuracaoSimplesNFSe: e.target.value === '' ? null : Number(e.target.value) })} size={230} />
                            </div>
                        </>
                    )}

                    {abaAtiva === 'certificado' && (
                        <div>
                            <div className="input-group mb-3">
                                <Input
                                    label="Status"
                                    value={configDFe.possuiCertificado ? 'Certificado salvo' : 'Nenhum certificado salvo'}
                                    disabled
                                    size={180}
                                />
                                <Input
                                    label="Validade"
                                    value={formatarValidadeCertificado()}
                                    disabled
                                    size={150}
                                />
                                <Input
                                    label="Dias restantes"
                                    value={getDiasValidadeCertificado() ?? ''}
                                    disabled
                                    size={130}
                                />
                            </div>
                            <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={() => setShowCertificadoModal(true)}
                                disabled={!configDFe?.id}
                            >
                                {configDFe.possuiCertificado ? 'Alterar certificado' : 'Salvar certificado'}
                            </button>
                        </div>
                    )}
                </form>
            </ModalBody>
            <ModalFooter>
                <ModalActions >
                    <ModalActionButton className="btn btn-primary" onClick={handleSave} action="save">
                        Salvar
                    </ModalActionButton>
                    <ModalActionButton className="btn btn-danger" onClick={handleDelete} disabled={novoReg} action="delete">
                        Excluir
                    </ModalActionButton>
                    <ModalActionButton className="btn btn-secondary" onClick={handleNovo} disabled={novoReg} action="new">
                        Novo
                    </ModalActionButton>
                </ModalActions>
            </ModalFooter>

            <ConfigDFeSearch
                show={showSearchModal}
                onHide={() => setShowSearchModal(false)}
                onSelectConfigDFe={(configSel) => {
                    setConfigDFe(configSel);
                    setShowSearchModal(false);
                    handleEditar(configSel.id);
                }}
            />

            <ConfigDFeCertificadoModal
                show={showCertificadoModal}
                onHide={() => setShowCertificadoModal(false)}
                configDFe={configDFe}
                onCertificadoSalvo={(configAtualizada) => setConfigDFe(configAtualizada)}
            />
        </ModalContainer>
    );
};

export default ConfigDFeCad;
